import { notify } from '@/lib/notify';
import {
  useAddLinkSource,
  useAddTextSource,
  useSourcesList,
  useUploadFileSource,
} from '@/hooks/lesson/useSources';
import { AttachSourceModal } from './AttachSourceModal';
import type { StagedSource } from './types';

interface InLessonAttachSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string;
}

/**
 * Thin wrapper that adapts a StagedSource into the matching mutation hook.
 *
 * Errors thrown out of `onAdd` propagate back into the modal so the active
 * tab can surface them inline (no toast spam). Successes resolve and let
 * the modal clear its form / close as appropriate.
 */
export function InLessonAttachSourceModal({
  isOpen,
  onClose,
  lessonId,
}: InLessonAttachSourceModalProps) {
  const uploadFile = useUploadFileSource(lessonId);
  const addLink = useAddLinkSource(lessonId);
  const addText = useAddTextSource(lessonId);
  // The plan's cap counts sources already added to this lesson, so the
  // modal refuses an extra file up front instead of after the upload.
  const { slotsLeft } = useSourcesList(lessonId);

  const handleAdd = async (source: StagedSource) => {
    if (!lessonId) {
      throw new Error('Lesson id missing - cannot add source.');
    }
    switch (source.kind) {
      case 'file':
        await uploadFile.mutateAsync(source.file);
        notify.success('File uploaded - processing...');
        break;
      case 'link':
        await addLink.mutateAsync({ url: source.url, name: source.name });
        notify.success('Link added - processing...');
        break;
      case 'text':
        await addText.mutateAsync({
          text: source.text,
          name: source.name,
          description: source.description,
        });
        notify.success('Text added - processing...');
        break;
    }
  };

  return (
    <AttachSourceModal
      isOpen={isOpen}
      onClose={onClose}
      onAdd={handleAdd}
      remainingFileSlots={slotsLeft ?? undefined}
    />
  );
}
